import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Store references to all GSAP instances for cleanup
let globalTimelines = new Set();
let globalScrollTriggers = new Set();
let globalScrollSmoother = null;
let globalSplitTexts = new Set();

/**
 * Register a timeline for global cleanup
 */
export const registerTimeline = (timeline) => {
  if (timeline && typeof timeline.kill === 'function') {
    globalTimelines.add(timeline);
  }
};

/**
 * Register a ScrollTrigger for global cleanup
 */
export const registerScrollTrigger = (trigger) => {
  if (trigger && typeof trigger.kill === 'function') {
    globalScrollTriggers.add(trigger);
  }
};

/**
 * Register ScrollSmoother for global cleanup
 */
export const registerScrollSmoother = (smoother) => {
  globalScrollSmoother = smoother;
};

/**
 * Register a SplitText instance for global cleanup
 */
export const registerSplitText = (splitText) => {
  if (splitText && typeof splitText.revert === 'function') {
    globalSplitTexts.add(splitText);
  }
};

/**
 * Unregister a timeline from cleanup tracking
 */
export const unregisterTimeline = (timeline) => {
  globalTimelines.delete(timeline);
};

/**
 * Unregister a ScrollTrigger from cleanup tracking
 */
export const unregisterScrollTrigger = (trigger) => {
  globalScrollTriggers.delete(trigger);
};

/**
 * Unregister ScrollSmoother from cleanup tracking
 */
export const unregisterScrollSmoother = () => {
  globalScrollSmoother = null;
};

/**
 * Unregister a SplitText from cleanup tracking
 */
export const unregisterSplitText = (splitText) => {
  globalSplitTexts.delete(splitText);
};

/**
 * Comprehensive GSAP cleanup function
 * This should be called on every page navigation
 */
export const cleanupAllGSAP = () => {
  try {
    // Kill all registered timelines
    globalTimelines.forEach((timeline) => {
      try {
        if (timeline && typeof timeline.kill === 'function') {
          timeline.kill();
        }
      } catch (error) {
        console.warn('Error killing timeline:', error);
      }
    });
    globalTimelines.clear();

    // Kill all registered ScrollTriggers
    globalScrollTriggers.forEach((trigger) => {
      try {
        if (trigger && typeof trigger.kill === 'function') {
          trigger.kill();
        }
      } catch (error) {
        console.warn('Error killing ScrollTrigger:', error);
      }
    });
    globalScrollTriggers.clear();

    // Kill ScrollSmoother
    if (globalScrollSmoother && typeof globalScrollSmoother.kill === 'function') {
      try {
        globalScrollSmoother.kill();
      } catch (error) {
        console.warn('Error killing ScrollSmoother:', error);
      }
    }
    globalScrollSmoother = null;

    // Revert all SplitText instances
    globalSplitTexts.forEach((splitText) => {
      try {
        if (splitText && typeof splitText.revert === 'function') {
          splitText.revert();
        }
      } catch (error) {
        console.warn('Error reverting SplitText:', error);
      }
    });
    globalSplitTexts.clear();

    // Kill all ScrollTriggers globally (fallback)
    try {
      ScrollTrigger.getAll().forEach((trigger) => {
        if (trigger && typeof trigger.kill === 'function') {
          trigger.kill();
        }
      });
      ScrollTrigger.clearScrollMemory();
      ScrollTrigger.refresh();
    } catch (error) {
      console.warn('Error in global ScrollTrigger cleanup:', error);
    }

    // Kill all GSAP animations globally (fallback)
    try {
      gsap.killTweensOf('*');
    } catch (error) {
      console.warn('Error in global GSAP cleanup:', error);
    }

    console.log('GSAP cleanup completed successfully');
  } catch (error) {
    console.error('Error during GSAP cleanup:', error);
  }
};

/**
 * Reset all tracking sets (useful for debugging)
 */
export const resetCleanupTracking = () => {
  globalTimelines.clear();
  globalScrollTriggers.clear();
  globalScrollSmoother = null;
  globalSplitTexts.clear();
};

/**
 * Get current cleanup statistics
 */
export const getCleanupStats = () => {
  return {
    timelines: globalTimelines.size,
    scrollTriggers: globalScrollTriggers.size,
    scrollSmoother: globalScrollSmoother ? 1 : 0,
    splitTexts: globalSplitTexts.size
  };
};
